import { useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription, AlertTitle } from "@veil/ui/components/alert";
import { Button } from "@veil/ui/components/button";
import { CheckCircle2, Loader2, ServerCog, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { RemoteWorkerSnippet } from "@/components/workers/remote-worker-snippet";
import { AddWorkerWizard } from "@/pages/workers/deploy";
import {
	toComposeInfo,
	useProvisionInfo,
} from "@/pages/workers/deploy/use-provision-info";

type ProvisionPhase = "idle" | "running" | "succeeded" | "failed";

/**
 * Step 4: add a worker. Primary action is the one-click LOCAL provision
 * (docker.sock) which pulls + runs a cluster-mode worker and registers it.
 * A copy-paste compose snippet is offered for remote / BYO hosts, and the
 * wizard's "Already running" target records one once it is up.
 */
export function AddWorkerStep({
	onNext,
	onBack,
}: {
	onNext: () => void;
	onBack: () => void;
}) {
	const queryClient = useQueryClient();
	const [phase, setPhase] = useState<ProvisionPhase>("idle");
	const [log, setLog] = useState("");
	const esRef = useRef<EventSource | null>(null);

	const info = useProvisionInfo();

	useEffect(() => {
		return () => esRef.current?.close();
	}, []);

	const streamOperation = (operationId: string) => {
		const es = new EventSource(`/api/operations/${operationId}/log/stream`);
		esRef.current = es;
		es.addEventListener("log", (e) => {
			setLog((prev) => prev + (e as MessageEvent).data);
		});
		es.addEventListener("status", (e) => {
			try {
				const payload = JSON.parse((e as MessageEvent).data) as {
					status: string;
				};
				setPhase(payload.status === "succeeded" ? "succeeded" : "failed");
			} catch {
				/* ignore */
			}
		});
		es.addEventListener("end", () => {
			es.close();
			queryClient.invalidateQueries({ queryKey: ["cluster"] });
		});
		es.onerror = () => es.close();
	};

	return (
		<div>
			{info.data && !info.data.hasOrgKey && (
				<Alert variant="destructive" className="mb-4">
					<TriangleAlert className="size-4" />
					<AlertTitle>Org key not set</AlertTitle>
					<AlertDescription>
						One-click local provisioning needs <code>VEIL_KEY</code> on the
						cluster-manager. Set it and restart, or add a worker manually below.
						You can also skip this step and add workers later.
					</AlertDescription>
				</Alert>
			)}

			<div className="rounded-lg border border-border/70 p-5">
				<div className="mb-4 flex items-start gap-3">
					<ServerCog className="mt-0.5 size-5 text-primary" />
					<div>
						<h3 className="text-sm font-semibold">Add a worker</h3>
						<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
							Workers launch and hold the browsers. This pulls the image, starts
							the container and registers it with the load balancer.
						</p>
					</div>
				</div>

				{/* Same wizard the Workers page uses. Here it streams the log in
				    place rather than navigating, so the operator stays in onboarding. */}
				{phase === "running" ? (
					<p className="flex items-center gap-2 text-sm text-muted-foreground">
						<Loader2 className="size-4 animate-spin" /> Deploying…
					</p>
				) : (
					<AddWorkerWizard
						onStarted={streamOperation}
						onRegistered={() => setPhase("succeeded")}
					/>
				)}

				{phase === "succeeded" && (
					<Alert className="mt-4">
						<CheckCircle2 className="size-4" />
						<AlertTitle>Worker added</AlertTitle>
						<AlertDescription>
							The worker is registered and the LB has been reloaded. Continue to
							review, or add another.
						</AlertDescription>
					</Alert>
				)}

				{log && (
					<pre className="mt-4 max-h-52 overflow-auto rounded-md bg-muted p-3 font-mono text-[0.7rem] leading-relaxed">
						{log}
					</pre>
				)}
			</div>

			<div className="mt-5 rounded-lg border border-dashed border-border/70 p-5">
				<h3 className="text-sm font-semibold">
					Add a worker on another machine
				</h3>
				<p className="mt-1 mb-3 text-xs text-muted-foreground">
					Run this on that machine, then record it here with "Already running".
				</p>
				{info.data ? (
					<RemoteWorkerSnippet
						info={toComposeInfo(info.data)}
						hostname="worker-1"
					/>
				) : null}
			</div>

			<div className="mt-6 flex items-center justify-between gap-2">
				<Button variant="ghost" onClick={onBack} disabled={phase === "running"}>
					Back
				</Button>
				<Button
					variant={phase === "succeeded" ? "default" : "secondary"}
					onClick={onNext}
					disabled={phase === "running"}
				>
					{phase === "succeeded" ? "Continue" : "Skip for now"}
				</Button>
			</div>
		</div>
	);
}
